/**
 * ResolutionSign — secure signing flow for a single resolution.
 *
 * Reads the resolution from `GET /v1/resolutions/{rid}` via `useResolution(rid)`
 * and signs by POSTing to `/v1/resolutions/{rid}/signatures` with the step-up
 * gate plumbed via the shared `ApiError`.
 *
 * Route shape: /app/trusts/$id/resolutions/$rid/sign  (both ids required).
 *
 * Signing state machine:
 *   idle -> (all 3 checked + click Sign) -> signing -> success | error
 *   STEP_UP_REQUIRED -> step-up modal opens in place + replays the signature
 *   ALREADY_SIGNED / RESOLUTION_CLOSED -> success (receipt UI)
 *
 * 2026-06-24 (app polish): a sober deep "secure signing" band is the focal
 * anchor (step-up-PROTECTED chip + the amount it authorises + signer progress),
 * the fingerprint is elevated as the authenticity moment, the two columns stack
 * on narrow widths, and all user-facing copy is white-label-safe (no hardcoded
 * "Elreign" — a firm's clients see only their firm). The in-meeting QR handoff
 * was removed: creating a resolution already pushes an "awaiting your signature"
 * notification to each signer's device (resolutions-create A2 push), so the
 * signer reaches this page from their own phone without a QR.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import { ScreenShell } from './screen-shell';
import { Icon } from '../icon';
import { useToastStore } from '../../lib/toast-store';
import { useSession } from '../../lib/use-session';
import {
  api,
  ApiError,
  useResolution,
  type ResolutionGetResponse,
} from '../../lib/api';
import { formatRand } from '../../lib/fmt-r';
import { mapError } from '../../lib/error-messages';
import { initialsOf } from '../../lib/format/initials';
import { getSigningAssertion, listPasskeys, isWebAuthnSupported } from '../../lib/webauthn';
import {
  getPlatformBiometricLabel,
  isPlatformAuthenticatorAvailable,
  type BiometricLabel,
} from '../../lib/webauthn-platform';
import { displayLegalName } from '../../lib/format/legal-name';
import { displayPersonName } from '../../lib/format/person-name';

// 2026-05-20 (F02-P-046): unsigned signers fall back to a neutral
// "Trustee (awaiting)" placeholder rather than a UUID tail.
const UNSIGNED_SIGNER_PLACEHOLDER = 'Trustee (awaiting)';

// 2026-08-22 (E2E 2026-08-21): the same person showed DIFFERENT avatar initials
// here and on the resolution detail screen. Both had their own copy of an
// identical `initialsFromName`, but this screen fell back to
// `initialsFromUserId` — the last two characters of a UUID — while the detail
// screen showed a neutral '··'. So an unsigned trustee appeared as e.g. "7F"
// here and "··" one click away: two different "identities" for one row, and the
// UUID tail reads like real initials without being any. Both screens now use
// the shared `initialsOf` helper with this same placeholder. Kept as a per-file
// constant to match UNSIGNED_SIGNER_PLACEHOLDER, which is already paired the
// same way across the two files — change one, change the other.
const UNSIGNED_INITIALS = '··';

// ── Sub-components ────────────────────────────────────────────────────────────

interface CheckboxRowProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}

function CheckboxRow({ checked, onChange, label }: CheckboxRowProps) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '4px 0' }}>
      <span style={{
        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
        border: checked ? 'none' : '1.5px solid var(--ink-3)',
        background: checked ? 'var(--primary-700)' : 'transparent',
        display: 'grid', placeItems: 'center',
        transition: 'all 200ms var(--ease-cushion)', marginTop: 1,
      }}>
        {checked && <Icon name="check" size={13} stroke={3} color="var(--surface)" />}
      </span>
      <span style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.45 }}>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
      />
    </label>
  );
}

interface AvatarProps {
  initials: string;
  signed: boolean;
  size?: number;
}

function Avatar({ initials, signed, size = 28 }: AvatarProps) {
  return (
    <div
      className={'avatar ' + (signed ? '' : 'unsigned')}
      style={{ width: size, height: size, fontSize: size <= 30 ? 11 : 13 }}
    >
      {initials}
    </div>
  );
}

// ── Signing spinner ───────────────────────────────────────────────────────────

function SigningSpinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, padding: '32px 0' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .sign-spin { width:44px; height:44px; border:3px solid var(--primary-100); border-top-color:var(--primary-700); border-radius:50%; animation:spin 800ms linear infinite; }
        @media (prefers-reduced-motion: reduce){ .sign-spin { animation:none; } }
      `}</style>
      <div className="sign-spin" />
      <div style={{ fontSize: 14, color: 'var(--ink-2)', fontWeight: 500, textAlign: 'center' }}>
        Verifying your signature…
      </div>
      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
        Do not close this window.
      </div>
    </div>
  );
}

// ── Success state — wax seal + receipt ────────────────────────────────────────

interface SuccessStateProps {
  onDone: () => void;
  chainHash: string;
  /** Show the "biometrically verified" evidential note (still ECTA s13). */
  biometricVerified: boolean;
}

function SuccessState({ onDone, chainHash, biometricVerified }: SuccessStateProps) {
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) + ' SAST';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '24px 0 16px' }}>
      <div
        aria-hidden="true"
        style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 35%, var(--accent-300), var(--accent-600))',
          boxShadow: '0 4px 16px rgba(132,99,47,0.35), 0 1px 0 rgba(255,255,255,0.15) inset',
          display: 'grid', placeItems: 'center', position: 'relative',
        }}
      >
        <div style={{ position: 'absolute', inset: 5, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.25)' }} />
        <Icon name="check" size={24} stroke={2.5} color="var(--primary-900)" />
      </div>

      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
          Resolution signed.
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
          Recorded on the audit chain at {timeStr}.
        </div>
      </div>

      {biometricVerified && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
          borderRadius: 999, background: 'var(--accent-50)', border: '1px solid var(--accent-300)',
        }}>
          <Icon name="shield" size={14} color="var(--accent-700)" />
          <span style={{ fontSize: 12.5, color: 'var(--accent-700)', fontWeight: 600 }}>
            Biometrically verified
          </span>
          {/* Legal-honesty: a biometric strengthens the EVIDENCE, it does not
              change the signature class. This stays an ordinary ECTA s13
              electronic signature — never labelled "advanced". */}
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>· electronic signature (ECTA s13)</span>
        </div>
      )}

      <div style={{
        width: '100%', background: 'var(--bg-2)', border: '1px solid var(--hairline)',
        borderRadius: 'var(--r-md)', padding: '14px 16px',
      }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Audit chain receipt</div>
        <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)', wordBreak: 'break-all', lineHeight: 1.6 }}>
          SHA-256 &middot; {chainHash}
        </div>
      </div>

      <button
        type="button"
        onClick={onDone}
        style={{
          width: '100%', padding: '13px 18px', borderRadius: 11, border: 'none',
          background: 'var(--primary-700)', color: 'var(--surface)',
          fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        Done
      </button>
    </div>
  );
}

// ── Sign mutation ─────────────────────────────────────────────────────────────

interface SignSuccessBody {
  signature: {
    id: string;
    resolutionId: string;
    userId: string;
    signedAt: string;
    signatureMethod: string;
  };
  resolution: {
    id: string;
    signers_signed: number;
    signers_total: number;
    status: 'awaiting' | 'completed' | 'cancelled' | 'draft';
  };
}

async function signResolution(
  resolutionId: string,
  signerReviewedHash: string,
  biometricAssertion?: AuthenticationResponseJSON,
): Promise<SignSuccessBody> {
  return api<SignSuccessBody>(
    `/api/v1/resolutions/${encodeURIComponent(resolutionId)}/signatures`,
    {
      method: 'POST',
      body: {
        method: 'simple-electronic',
        attestation: {
          platform: 'web',
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : undefined,
          // True only when a fresh biometric assertion rode along; the server
          // re-derives the authoritative value from actual verification.
          biometricUsed: Boolean(biometricAssertion),
        },
        // F-009 body-swap defense: the canonical hash the signer reviewed.
        signerReviewedHash,
        // Biometric EVIDENCE (Face ID / Touch ID / Hello / fingerprint). Present
        // only when the signer chose biometric signing. Recorded as ECTA s13
        // evidence server-side — never a legal-class upgrade.
        ...(biometricAssertion ? { webauthn: { response: biometricAssertion } } : {}),
      },
    },
  );
}

// ── Main component ────────────────────────────────────────────────────────────

type Phase = 'idle' | 'signing' | 'success';

export function ResolutionSign() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pushToast } = useToastStore();
  const { session } = useSession();
  const currentUserId = session?.user?.id;

  const params = useParams({ strict: false }) as { id?: string; rid?: string };
  const trustId = params.id;
  const resolutionId = params.rid;

  const { data, isLoading, isError, error } = useResolution(resolutionId);

  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [c3, setC3] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const allChecked = c1 && c2 && c3;

  // Biometric-signing affordance: offered ONLY when a platform authenticator is
  // available AND the signer has an enrolled passkey (queried from Elreign's own
  // backend, never Supabase). Otherwise the normal signing flow stands alone —
  // no dead button.
  const [biometric, setBiometric] = useState<BiometricLabel | null>(null);
  const [signedWithBiometric, setSignedWithBiometric] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        if (!isWebAuthnSupported()) return;
        const available = await isPlatformAuthenticatorAvailable();
        if (!available || cancelled) return;
        const passkeys = await listPasskeys();
        if (cancelled || passkeys.length === 0) return;
        setBiometric(getPlatformBiometricLabel());
      } catch {
        // No passkey / feature off / probe failure → normal signing flow.
        if (!cancelled) setBiometric(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signMutation = useMutation({
    mutationFn: async (vars: { useBiometric: boolean }) => {
      const hash = data?.resolution.canonicalHash;
      if (!hash) {
        return Promise.reject(
          new Error(
            'Resolution body not yet loaded; reload before signing (F-009 body-swap guard).',
          ),
        );
      }
      let assertion: AuthenticationResponseJSON | undefined;
      if (vars.useBiometric) {
        // Fresh gesture BOUND to this resolution. A cancel/timeout throws
        // NotAllowedError → handled in onError (graceful, offer fallback).
        assertion = await getSigningAssertion(resolutionId!);
      }
      return signResolution(resolutionId!, hash, assertion);
    },
    onSuccess: (result) => {
      setPhase('success');
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['resolutions'] });
      // useTrustResolutions (the trust's Resolutions tab) is keyed
      // ['trusts','resolutions',...] — a DIFFERENT prefix from ['resolutions'],
      // so it needs its own invalidation (mirrors usePatchResolution /
      // useCreateResolution) or the tab keeps showing this resolution as
      // "Awaiting" with a live "Sign now" button within the 30s stale window.
      void queryClient.invalidateQueries({ queryKey: ['trusts', 'resolutions'] });
      const time = new Date(result.signature.signedAt).toLocaleTimeString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit',
      });
      pushToast({
        kind: 'success',
        message: signedWithBiometric
          ? `Resolution signed and biometrically verified. Recorded on the audit chain at ${time} SAST.`
          : `Resolution signed. Recorded on the audit chain at ${time} SAST.`,
      });
    },
    onError: (err) => {
      setPhase('idle');
      // Browser WebAuthn ceremony cancel/timeout — NOT an error condition. Let
      // the signer retry or fall back to signing without biometrics.
      if (err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        setSignedWithBiometric(false);
        pushToast({
          kind: 'info',
          message: 'Biometric prompt cancelled. Try again, or sign without biometrics.',
        });
        return;
      }
      if (!(err instanceof ApiError)) {
        pushToast({ kind: 'error', message: mapError(err, 'generic') });
        return;
      }
      // A passkey vanished between page load and sign, or the feature is off →
      // fall back to the normal flow rather than dead-ending.
      if (err.code === 'NO_PASSKEY_ENROLLED' || err.code === 'BIOMETRIC_NOT_CONFIGURED') {
        setBiometric(null);
        setSignedWithBiometric(false);
        pushToast({
          kind: 'info',
          message: 'Biometric signing is unavailable. Sign without biometrics.',
        });
        return;
      }
      // STEP_UP_REQUIRED handled globally by StepUpInterceptor — the modal opens
      // in place and replays this signature. Skip the per-screen toast.
      if (err.isStepUpRequired) return;
      if (err.code === 'ALREADY_SIGNED' || err.code === 'RESOLUTION_CLOSED') {
        setPhase('success');
        pushToast({
          kind: 'success',
          message: 'This resolution is already signed and on the audit chain.',
        });
        return;
      }
      if (err.code === 'BODY_MISMATCH') {
        void queryClient.invalidateQueries({ queryKey: ['resolutions', 'detail', resolutionId] });
        pushToast({
          kind: 'error',
          message:
            'The resolution body changed since you reviewed it. Reload and re-review before signing.',
        });
        return;
      }
      pushToast({ kind: 'error', message: mapError(err, 'generic') });
    },
  });

  function handleSign(useBiometric: boolean) {
    if (!allChecked || phase !== 'idle' || !resolutionId) return;
    setSignedWithBiometric(useBiometric);
    setPhase('signing');
    signMutation.mutate({ useBiometric });
  }

  function navigateToResolutions() {
    void navigate({ to: `/app/trusts/${trustId}/resolutions` as never });
  }

  // ── Bail-out states (real data or honest error UI) ──

  if (!resolutionId || !trustId) {
    return (
      <ScreenShell
        activeNav="res"
        eyebrow="ERROR"
        title="Missing route parameters"
        sub="The signing screen needs both a trust id and a resolution id in the URL."
      >
        <div className="card" style={{ padding: 24 }}>
          <p>
            Open this screen from the dashboard banner or the trust&rsquo;s
            resolutions list. If the URL was edited by hand, double-check both
            the trust slug and resolution id.
          </p>
        </div>
      </ScreenShell>
    );
  }

  if (isLoading || !data) {
    return (
      <ScreenShell
        activeNav="res"
        eyebrow="LOADING"
        title="Loading resolution&hellip;"
        sub="Fetching the latest hash from the audit chain."
      >
        <div className="card" style={{ padding: 24 }}>
          {isError ? (
            <p style={{ color: 'var(--negative)' }}>
              {error instanceof ApiError && error.status === 404
                ? "We couldn't find that resolution in your trust."
                : 'Failed to load the resolution. Please try again.'}
            </p>
          ) : (
            <SigningSpinner />
          )}
        </div>
      </ScreenShell>
    );
  }

  // NOT-CIRCULATED GATE (E2E 2026-08-21). The backend now 409s
  // RESOLUTION_NOT_CIRCULATED for a draft — a draft has not been sent to
  // anyone, no signing-request email exists, and its body is still being
  // edited. Offering the sign affordance here would walk the signer into a
  // guaranteed error, so say the true thing and point at the fix. Only `draft`
  // is intercepted: completed / cancelled keep their existing receipt handling
  // (the phase machine maps RESOLUTION_CLOSED onto the success receipt).
  if (data.resolution.status === 'draft') {
    return (
      <ScreenShell
        activeNav="res"
        eyebrow="Not yet circulated"
        title="This resolution is still a draft"
        sub="It has to be circulated for signature before anyone can sign it."
      >
        <div className="card" style={{ padding: 24 }}>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0 }}>
            Nobody has been asked to sign <strong>{data.resolution.title}</strong> yet. The other
            signatories have had no notification, and the wording can still change. Open the
            resolution and choose <strong>Circulate for signature</strong> first.
          </p>
          <button
            type="button"
            className="btn"
            style={{ marginTop: 18 }}
            onClick={() =>
              void navigate({
                to: `/app/trusts/${trustId}/resolutions/${resolutionId}` as never,
              })
            }
          >
            Open the resolution
          </button>
        </div>
      </ScreenShell>
    );
  }

  return <ResolutionSignContent
    data={data}
    trustId={trustId}
    resolutionId={resolutionId}
    currentUserId={currentUserId}
    phase={phase}
    c1={c1} c2={c2} c3={c3}
    setC1={setC1} setC2={setC2} setC3={setC3}
    allChecked={allChecked}
    biometric={biometric}
    signedWithBiometric={signedWithBiometric}
    onSign={handleSign}
    onCancel={navigateToResolutions}
    onDone={navigateToResolutions}
  />;
}

// ── Real-data render ──────────────────────────────────────────────────────────

interface ContentProps {
  data: ResolutionGetResponse;
  trustId: string;
  resolutionId: string;
  currentUserId: string | undefined;
  phase: Phase;
  c1: boolean; c2: boolean; c3: boolean;
  setC1: (v: boolean) => void;
  setC2: (v: boolean) => void;
  setC3: (v: boolean) => void;
  allChecked: boolean;
  /** Platform-correct biometric label when biometric signing is offered, else null. */
  biometric: BiometricLabel | null;
  /** Whether the just-completed signature used biometric verification. */
  signedWithBiometric: boolean;
  onSign: (useBiometric: boolean) => void;
  onCancel: () => void;
  onDone: () => void;
}

function ResolutionSignContent(props: ContentProps) {
  const {
    data, currentUserId, phase, c1, c2, c3, setC1, setC2, setC3,
    allChecked, biometric, signedWithBiometric, onSign, onCancel, onDone,
  } = props;
  const { resolution, trustName, signatures } = data;

  const canonicalHash = resolution.canonicalHash;
  const fingerprint = canonicalHash.startsWith('sha256:')
    ? canonicalHash.slice('sha256:'.length, 'sha256:'.length + 8)
    : canonicalHash.slice(0, 8);

  const signedById = new Map(signatures.map((s) => [s.userId, s]));
  const callerDisplayName = currentUserId
    ? displayPersonName(signedById.get(currentUserId)?.userDisplayName) || null
    : null;

  const cream = 'var(--surface)';
  const softCream = 'color-mix(in srgb, var(--surface) 64%, transparent)';

  return (
    <ScreenShell
      activeNav="res"
      eyebrow="Secure signing"
      title={callerDisplayName ? <>Sign as <em>{callerDisplayName}</em></> : 'Sign this resolution'}
      sub="Check the fingerprint, then confirm the three statements to sign."
    >
      <style>{`
        .sign-cols{ display:grid; grid-template-columns:1.6fr 1fr; gap:22px; align-items:start; }
        .sign-panel-wrap{ position:sticky; top:24px; }
        @media (max-width:900px){ .sign-cols{ grid-template-columns:1fr; } .sign-panel-wrap{ position:static; } }
        @media (max-width:760px){ .sign-band{ flex-direction:column; align-items:flex-start !important; } .sign-band-side{ text-align:left !important; } .sign-band-side .sign-prog{ justify-content:flex-start !important; } }
      `}</style>

      {/* Secure-signing band — the focal anchor (sober, white-label) */}
      <section
        className="sign-band"
        style={{
          position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(135deg, var(--primary-700), var(--primary-800))',
          borderRadius: 22, padding: '26px 30px', marginBottom: 18,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 28,
          boxShadow: '0 26px 50px -32px color-mix(in srgb, var(--primary-800) 85%, transparent)',
        }}
      >
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(130% 170% at 94% -30%, color-mix(in srgb, var(--accent-400) 16%, transparent), transparent 58%)' }} />
        <div style={{ position: 'relative', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <Icon name="lock" size={15} color="var(--accent-300)" />
            <span className="eyebrow on-dark">Secure signing</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.01em',
              color: 'color-mix(in srgb, var(--surface) 86%, transparent)', background: 'color-mix(in srgb, var(--surface) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--surface) 16%, transparent)', borderRadius: 999, padding: '3px 9px 3px 7px',
            }}>
              <Icon name="check" size={11} stroke={3} color="#7DC59C" /> Step-up protected
            </span>
          </div>
          <h1 className="entity-name" style={{ fontWeight: 300, fontSize: 'clamp(24px, 3vw, 32px)', lineHeight: 1.2, color: cream, margin: '0 0 8px', letterSpacing: '-0.01em' }}>
            {resolution.title}
          </h1>
          <div style={{ fontSize: 13, color: softCream }}>
            Resolution {resolution.ref} · {displayLegalName(trustName) || 'Trust'}
          </div>
        </div>

        <div className="sign-band-side" style={{ position: 'relative', textAlign: 'right', flexShrink: 0 }}>
          {resolution.amount !== undefined && (
            <>
              <div className="eyebrow on-dark" style={{ marginBottom: 6 }}>Authorises</div>
              <div className="num" style={{ fontFamily: 'var(--serif)', fontWeight: 300, fontSize: 'clamp(26px, 3.4vw, 38px)', color: cream, lineHeight: 1, letterSpacing: '-0.01em' }}>
                {formatRand(resolution.amount, { cents: true })}
              </div>
            </>
          )}
          <div className="sign-prog" style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', marginTop: resolution.amount !== undefined ? 14 : 0 }}>
            <div style={{ display: 'flex' }}>
              {resolution.signerUserIds.slice(0, 5).map((uid) => {
                const sig = signedById.get(uid);
                const initials = sig?.userDisplayName ? initialsOf(sig.userDisplayName) : UNSIGNED_INITIALS;
                return (
                  <span key={uid} style={{
                    width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center',
                    fontSize: 9.5, fontWeight: 600, border: '2px solid var(--primary-700)', marginLeft: -7,
                    background: sig ? 'var(--accent-400)' : 'color-mix(in srgb, var(--surface) 16%, transparent)',
                    color: sig ? 'var(--primary-900)' : 'color-mix(in srgb, var(--surface) 70%, transparent)',
                  }}>{initials}</span>
                );
              })}
            </div>
            <span style={{ fontSize: 12, color: softCream, marginLeft: 4 }}>
              {resolution.signers_signed} of {resolution.signers_total} signed
            </span>
          </div>
        </div>
      </section>

      {/* Fingerprint authenticity */}
      <section style={{
        display: 'flex', gap: 18, alignItems: 'flex-start',
        border: '1px solid var(--accent-300)', background: 'linear-gradient(180deg, var(--accent-50), var(--surface))',
        borderRadius: 16, padding: '20px 24px', marginBottom: 18,
      }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: 'var(--accent-100)', color: 'var(--accent-700)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <Icon name="shield" size={20} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="eyebrow" style={{ color: 'var(--accent-700)' }}>Document fingerprint</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 22, color: 'var(--ink)', marginTop: 7, fontWeight: 500, letterSpacing: '-0.01em' }}>
            {fingerprint}<span style={{ fontFamily: 'var(--sans)', color: 'var(--ink-3)', fontSize: 13.5, marginLeft: 10, fontWeight: 400 }}>· this is exactly what you&apos;re signing.</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--accent-700)', marginTop: 9, lineHeight: 1.55 }}>
            If this fingerprint doesn&apos;t match what you received out of band, <b>do not sign</b>. Contact your firm&apos;s trust office immediately.
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-4)', marginTop: 8, wordBreak: 'break-all', letterSpacing: '-0.01em' }}>
            {canonicalHash}
          </div>
        </div>
      </section>

      <div className="sign-cols">
        {/* Resolution body */}
        <div className="card" style={{ padding: '34px 40px' }}>
          <div className="eyebrow">Resolution body</div>
          <h2 className="entity-name" style={{ fontWeight: 300, fontSize: 27, lineHeight: 1.25, margin: '8px 0 22px', letterSpacing: '-0.01em' }}>
            {resolution.title}
          </h2>
          <div style={{ fontSize: 15.5, lineHeight: 1.7, color: 'var(--ink-2)', maxWidth: 600, whiteSpace: 'pre-wrap' }}>
            {resolution.body}
          </div>
          <div style={{ marginTop: 26, paddingTop: 16, borderTop: '1px solid var(--hairline)', display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-3)' }}>
            <span>Signed · audit-trail enabled</span>
            <span>{resolution.ref}</span>
          </div>
        </div>

        {/* Sign panel */}
        <div className="sign-panel-wrap">
          <div className="card" style={{ padding: 26 }}>
            {phase === 'signing' ? (
              <SigningSpinner />
            ) : phase === 'success' ? (
              <SuccessState onDone={onDone} chainHash={canonicalHash} biometricVerified={signedWithBiometric} />
            ) : (
              <>
                <div className="eyebrow">Important details</div>
                <div style={{ marginTop: 13, fontSize: 14, lineHeight: 1.6, color: 'var(--ink-2)' }}>
                  <div style={{ marginBottom: 12 }}>
                    {resolution.amount !== undefined ? (
                      <>This resolution authorises a distribution of <b style={{ color: 'var(--ink)' }}>{formatRand(resolution.amount, { cents: true })}</b>.</>
                    ) : (
                      <>Read the resolution body and confirm you understand its effect.</>
                    )}
                  </div>
                  <div>After signing, this becomes <b style={{ color: 'var(--ink)' }}>legally binding</b> and is written to the audit chain.</div>
                </div>

                <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--hairline)' }}>
                  <div className="eyebrow" style={{ marginBottom: 12 }}>
                    Signers ({resolution.signers_signed} of {resolution.signers_total})
                  </div>
                  {resolution.signerUserIds.map((uid) => {
                    const sig = signedById.get(uid);
                    const isYou = uid === currentUserId;
                    const name = displayPersonName(sig?.userDisplayName) || (isYou ? 'You' : UNSIGNED_SIGNER_PLACEHOLDER);
                    const initials = sig?.userDisplayName ? initialsOf(sig.userDisplayName) : UNSIGNED_INITIALS;
                    return (
                      <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                        <Avatar initials={initials} signed={!!sig} size={28} />
                        <span style={{ fontSize: 13.5, color: sig ? 'var(--ink)' : 'var(--ink-3)', flex: 1 }}>{name}</span>
                        {sig ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--positive)', fontWeight: 600, letterSpacing: '0.08em' }}>
                            <Icon name="check" size={11} stroke={2.5} color="var(--positive)" />
                            SIGNED
                          </span>
                        ) : isYou ? (
                          <span style={{ fontSize: 11, color: 'var(--accent-700)', fontWeight: 600, letterSpacing: '0.08em' }}>YOU</span>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 500, letterSpacing: '0.06em' }}>AWAITING</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: 22, paddingTop: 20, borderTop: '1px solid var(--hairline)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <CheckboxRow checked={c1} onChange={setC1} label="I have read this resolution" />
                  <CheckboxRow checked={c2} onChange={setC2} label={callerDisplayName ? `I am ${callerDisplayName}` : 'I confirm I am the named signer'} />
                  <CheckboxRow checked={c3} onChange={setC3} label="I authorise this resolution" />
                </div>

                {biometric ? (
                  <>
                    {/* Biometric is offered: platform-correct PRIMARY action
                        (e.g. "Sign with Face ID" / "Windows Hello" / fingerprint). */}
                    <button
                      type="button"
                      disabled={!allChecked}
                      onClick={() => onSign(true)}
                      style={{
                        width: '100%', marginTop: 22, padding: '14px 18px', borderRadius: 12, border: 'none',
                        background: allChecked ? 'var(--primary-700)' : 'var(--surface-2)',
                        color: allChecked ? 'var(--surface)' : 'var(--ink-3)',
                        fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 600,
                        cursor: allChecked ? 'pointer' : 'not-allowed',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        transition: 'all 240ms var(--ease-cushion)',
                      }}
                    >
                      <Icon name="shield" size={17} stroke={2} />
                      {allChecked ? biometric.buttonLabel : 'Confirm all three to sign'}
                    </button>
                    {/* Never a dead-end: sign without biometrics if the gesture
                        is cancelled or the signer prefers. */}
                    <button
                      type="button"
                      disabled={!allChecked}
                      onClick={() => onSign(false)}
                      style={{
                        width: '100%', marginTop: 8, padding: '12px 18px', borderRadius: 12,
                        border: '1px solid var(--hairline)', background: 'transparent',
                        color: allChecked ? 'var(--ink)' : 'var(--ink-3)',
                        fontFamily: 'var(--sans)', fontSize: 13.5,
                        cursor: allChecked ? 'pointer' : 'not-allowed',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}
                    >
                      <Icon name="sign" size={15} stroke={2} />
                      Sign without biometrics
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={!allChecked}
                    onClick={() => onSign(false)}
                    style={{
                      width: '100%', marginTop: 22, padding: '14px 18px', borderRadius: 12, border: 'none',
                      background: allChecked ? 'var(--primary-700)' : 'var(--surface-2)',
                      color: allChecked ? 'var(--surface)' : 'var(--ink-3)',
                      fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 600,
                      cursor: allChecked ? 'pointer' : 'not-allowed',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      transition: 'all 240ms var(--ease-cushion)',
                    }}
                  >
                    <Icon name="sign" size={16} stroke={2} />
                    {allChecked
                      ? (callerDisplayName ? `Sign as ${callerDisplayName}` : 'Sign this resolution')
                      : 'Confirm all three to sign'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onCancel}
                  style={{
                    width: '100%', marginTop: 8, padding: '12px 18px', borderRadius: 12,
                    border: '1px solid var(--hairline)', background: 'transparent',
                    color: 'var(--ink-2)', fontFamily: 'var(--sans)', fontSize: 13.5, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}
